import type { ReactNode } from 'react';
import { Breadcrumbs, Text } from '@gravity-ui/uikit';
import { isPlainClick, useGo } from './router-link.js';

/**
 * Страница консоли: крошки над заголовком, заголовок с действиями справа,
 * под ним — содержимое. Так устроена каждая страница Yandex Cloud: где я,
 * что это, что здесь можно сделать — в одной строке, без заставок.
 */

export interface Crumb {
  label: string;
  to: string;
}

export function Page({
  title,
  crumbs,
  description,
  actions,
  aside,
  children,
}: {
  title: ReactNode;
  crumbs?: readonly Crumb[];
  description?: ReactNode;
  actions?: ReactNode;
  /** Рядом с заголовком: состояние, ярлык. */
  aside?: ReactNode;
  children: ReactNode;
}) {
  const go = useGo();

  return (
    <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-6 px-4 pt-5 pb-10 md:px-8 md:pt-6">
      <header className="flex flex-col gap-2">
        {crumbs === undefined || crumbs.length === 0 ? null : (
          <Breadcrumbs>
            {crumbs.map((crumb) => (
              <Breadcrumbs.Item
                key={crumb.to}
                href={crumb.to}
                onClick={(event) => {
                  if (!isPlainClick(event)) return;
                  event.preventDefault();
                  go(crumb.to);
                }}
              >
                {crumb.label}
              </Breadcrumbs.Item>
            ))}
            <Breadcrumbs.Item disabled>{typeof title === 'string' ? title : 'Текущая страница'}</Breadcrumbs.Item>
          </Breadcrumbs>
        )}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <Text as="h1" variant="header-2" className="min-w-0 break-words">
              {title}
            </Text>
            {aside}
          </div>
          {actions === undefined ? null : <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {description === undefined ? null : (
          <Text as="p" color="secondary" className="max-w-[72ch]">
            {description}
          </Text>
        )}
      </header>
      {children}
    </div>
  );
}

/**
 * Раздел страницы — светлая панель с тонкой рамкой, как блоки обзора ресурса
 * в консоли. Разделы не вкладываются друг в друга.
 */
export function Section({
  title,
  actions,
  children,
  flush = false,
  className = '',
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  /** Без внутренних полей — для таблиц во всю ширину раздела. */
  flush?: boolean;
  className?: string;
}) {
  return (
    <section
      className={`flex min-w-0 flex-col rounded-xl border border-[var(--g-color-line-generic)] bg-[var(--g-color-base-background)] ${className}`}
    >
      {title === undefined && actions === undefined ? null : (
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-1">
          {title === undefined ? <span /> : <Text as="h2" variant="subheader-3">{title}</Text>}
          {actions === undefined ? null : <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={flush ? 'min-w-0 pt-2 pb-2' : 'min-w-0 px-5 pt-3 pb-5'}>{children}</div>
    </section>
  );
}
